// Imports
import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import useCachedState from './use-cached-state';
import Input from '../components/forms/fields/input';
import Select from '../components/forms/fields/select';
import helperFunctions from './helper-functions';
import styled from 'styled-components/macro';
import theme from '../../../../shareables/theme';
import PageNavigation from '../components/resources/page-navigation';
import uniq from 'lodash/uniq';
import { faMagnifyingGlass, faBarsStaggered, faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import useRecoveredState from './use-recovered-state';
import get from 'lodash/get';
import useUpdateOnlyEffect from './use-update-only-effect';
import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import uniqueId from 'lodash/uniqueId';
import { z } from 'zod';


// Styled components
export const Pagination = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	
	@media (max-width: ${theme.breakpoints.desktop}px) {
		flex-direction: column;
	}
`;

export const DisplayingText = styled.div`
	@media (max-width: ${theme.breakpoints.desktop}px) {
		margin-bottom: 1em;
	}
`;


// Define the options
type KeyReducer<T> = (item: T) => string;

export interface Sorting {
	/** The column that’s being sorted. */
	column: string;
	
	
	/** The sort direction. */
	direction: 'ascending' | 'descending';
}

interface BaseOptions<T> {
	/** The list of page sizes the user can choose from. */
	pageSizes: number[];
	
	
	/** The plural, all-lowercase descriptor of the items in the list, to be used in various places. For example, if building a list of users, this would be set to `'users'`. */
	pluralDescriptor: string;
	
	
	/** The keys in each item to match search queries against. If a string, can be any valid path accepted by Lodash’s `get()` function. If a function, it’s provided with an item and expects a string in return. */
	searchableKeys?: (string | KeyReducer<T>)[];
	
	
	/** Fallback values to use for string-based `searchableKeys` whose values are non-strings. For example, values that are `undefined` or `null`. */
	missingSearchableKeyFallbacks?: {
		[key: string]: string;
	};
	
	
	/** A custom sorting function that must be memoized. See the `Array.prototype.sort()` documentation for more details. Also handed a `sorting` object as the third parameter, which indicates the current sort column + direction. */
	memoizedSortFunction?: (a: T, b: T, sorting: Sorting) => number;
	
	
	/** A custom filtering function. See the `Array.prototype.filter()` documentation for more details. */
	filterFunction?: (item: T) => boolean;
	
	
	/** Props to be passed-along to the `<PageNavigation />` component. */
	pageNavigationProps?: (typeof PageNavigation)['defaultProps'];
	
	
	/** Used to define the initial column to sort on, along with the direction. */
	initialSorting?: Sorting;
	
	
	/** Used to define an initial search query. Mostly used for Storybook stories. */
	initialSearchQuery?: string;
	
	
	/** Used to set the initial page size. Mostly used for Storybook stories. */
	initialPageSize?: number;
	
	
	/** Used to set the initial items. Mostly used for Storybook stories. */
	initialItems?: T[];
}

interface CachingOptions<T> extends BaseOptions<T> {
	/** The key to use when storing the list of items in key-value storage (kabob case). */
	cacheStorageKey: string;
	disableCaching?: never;
}

interface DisableCachingOptions<T> extends BaseOptions<T> {
	/** Whether to disable caching. */
	disableCaching: true;
	cacheStorageKey?: never;
}

export type Options<T> = CachingOptions<T> | DisableCachingOptions<T>;


// Define the return object
interface PaginationReturnObject<T> {
	/** The list of items, after filtering, sorting, slicing, etc. */
	items: T[] | null;
	
	
	/** The list of items, after filtering, sorting, etc., but before slicing */
	unslicedItems: T[] | null;
	
	
	/** The setter used to update the list of items as needed. */
	setItems: React.Dispatch<React.SetStateAction<T[] | null>>;
	
	
	/** The current search query. If none, contains an empty string. */
	searchQuery: string;
	
	
	/** The setter used to update the search query. */
	setSearchQuery: (query: string) => void;
	
	
	/** The current sorting details. */
	sorting: Sorting | undefined;
	
	
	/** Changes the sorting column to the provided column name. */
	setSortingColumn: (column: string, initialDirection?: Sorting['direction']) => void;
	
	
	/** The search field that can be shown, if desired. */
	searchField: JSX.Element;
	
	
	/** The page size field that can be shown. Will present the choices from `pageSizes` that make sense based on the total number of items. Will be `null` when it should not be shown. */
	pageSizeField: React.ReactElement<any, string | React.JSXElementConstructor<any>> | null;
	
	
	/** The pagination component that should be shown. If the number of items to display is less than or equal to the smallest page size choice, then this will be `null`. */
	paginationComponent: React.ReactElement<any, string | React.JSXElementConstructor<any>> | null;
}


/** A custom Hook that handles all the logic surrounding pagination, sorting, and filtering of lists of data */
export default function usePagination<T = void>({
	cacheStorageKey,
	pageSizes,
	searchableKeys,
	missingSearchableKeyFallbacks,
	memoizedSortFunction,
	filterFunction,
	pageNavigationProps,
	initialSorting,
	initialSearchQuery,
	initialPageSize,
	initialItems,
	disableCaching,
	pluralDescriptor,
}: Options<T>): PaginationReturnObject<T> {
	// Sort page sizes and remove duplicates
	const sortedPageSizes = uniq(pageSizes.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)));
	
	
	// Generate unique ID
	const uniqueIdentifier = useRef(uniqueId());
	
	
	// Use state
	const [nonCachedItems, setNonCachedItems] = useState<T[] | null>(initialItems ?? null);
	const [cachedItems, setCachedItems] = useCachedState<T[] | null>(initialItems ?? null, cacheStorageKey ?? 'ignore');
	
	const [searchQuery, setSearchQuery] = useRecoveredState(
		initialSearchQuery ?? '',
		`${cacheStorageKey ?? uniqueIdentifier.current}-search-query`
	);
	
	const [pageSize, setPageSize] = useRecoveredState(
		initialPageSize ?? sortedPageSizes[0],
		`${cacheStorageKey ?? uniqueIdentifier.current}-page-size`
	);
	
	const [pageIndex, setPageIndex] = useRecoveredState(0, `${cacheStorageKey ?? uniqueIdentifier.current}-page-index`);
	
	const [sorting, setSorting] = useRecoveredState<Sorting | undefined>(
		initialSorting,
		`${cacheStorageKey ?? uniqueIdentifier.current}-sort`
	);
	
	
	// Use ref
	const pageSizeSetter = useRef<(newValue: string | number) => void>();
	
	
	// Decide whether to cache or not
	const [items, setItems] = useMemo(
		() => (disableCaching ? [nonCachedItems, setNonCachedItems] : [cachedItems, setCachedItems]),
		[disableCaching, nonCachedItems, cachedItems, setCachedItems]
	);
	
	
	// Catch problems
	if (searchQuery && searchableKeys === undefined) {
		throw new Error('If you provide a `searchQuery`, you must also specify `searchableKeys`');
	}
	
	if (memoizedSortFunction && initialSorting === undefined) {
		throw new Error('If you provide a `memoizedSortFunction`, you must also specify `initialSorting`');
	}
	
	/* eslint-disable-next-line array-callback-return */
	sortedPageSizes.map((pageSize) => {
		if (!Number.isInteger(pageSize)) {
			throw new Error('Page sizes must be integers');
		}
		
		if (pageSize < 1) {
			throw new Error('Page sizes must be greater than or equal to 1');
		}
	});
	
	if (items?.length && typeof items[0] !== 'object') {
		throw new Error('Items must be objects');
	}
	
	
	// Throw out all page sizes that are greater than or equal to the number of items, minus the first one
	let foundGreater = false;
	
	const filteredPageSizes = sortedPageSizes.filter((pageSize) => {
		if (foundGreater) {
			return false;
		}
		
		if (items === null) {
			return false;
		}
		
		if (pageSize >= items.length) {
			foundGreater = true;
		}
		
		return true;
	});
	
	
	// Handle when the page size is greater than the available options
	const pageSizeIsGreaterThanLargestOption = pageSize > filteredPageSizes[filteredPageSizes.length - 1];
	const pageSizeAfterFiltering = pageSizeIsGreaterThanLargestOption
		? filteredPageSizes[filteredPageSizes.length - 1]
		: pageSize;
	
	useEffect(() => {
		if (pageSizeIsGreaterThanLargestOption) {
			pageSizeSetter.current?.(filteredPageSizes[filteredPageSizes.length - 1]); // State updates cannot happen in the render phase so this needs to be in a `useEffect`
		}
	});
	
	
	// Build search index
	const [searchIndex, setSearchIndex] = useRecoveredState<string[] | null>(
		null,
		`${cacheStorageKey ?? uniqueIdentifier.current}-search-index`
	);
	
	const previousStringifiedItemsForSearchIndex = useRef<string>();
	
	/* eslint-disable-next-line react-hooks/exhaustive-deps */
	useEffect(() => {
		// Don't do any work if no searchable keys or if items have not loaded
		if (!searchableKeys || !items) {
			return;
		}
		
		
		// Stop if items haven't changed
		//  -> Stringifying a large number of items is slow, so doing it inside the asynchronous
		//     `useEffect()` is much more performant than doing it outside of it
		const newStringifiedItems = JSON.stringify(items);
		
		if (newStringifiedItems === previousStringifiedItemsForSearchIndex.current) {
			return;
		}
		
		previousStringifiedItemsForSearchIndex.current = newStringifiedItems;
		
		
		// Initialize
		const promises = [];
		
		
		// Loop over every item
		//  -> The promise/setTimeout pattern is required to prevent blocking the main thread and freezing the UI
		for (const item of items) {
			promises.push(
				// There should not be closure issues with `setTimeout`
				// eslint-disable-next-line @typescript-eslint/no-loop-func
				new Promise<string>((resolve) => {
					window.setTimeout(() => {
						// Initialize
						let concatenatedString = '';
						
						
						// Loop over ever searchable key
						for (const key of searchableKeys) {
							// Retrieve value
							let value: string | null | undefined;
							
							if (typeof key === 'string') {
								value = z.string().optional().nullable().parse(get(item, key));
							} else {
								value = key(item);
							}
							
							
							// Manipulate value as needed
							if (typeof value !== 'string' && typeof key === 'string') {
								value = get(missingSearchableKeyFallbacks, key, '');
							}
							
							if (value && /^[0-9]{4}-[0-9]{2}-[0-9]{2}(T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z)?$/.test(value)) {
								// Convert to human-readable format
								let humanReadableDateTime = new Date(value).toLocaleDateString(undefined, {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
									timeZone: /^[0-9]{4}-[0-9]{2}-[0-9]{2}?$/.test(value) ? 'UTC' : undefined,
								});
								
								if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z$/.test(value)) {
									humanReadableDateTime += ` ${new Date(value).toLocaleTimeString(undefined, {
										hour: 'numeric',
										minute: '2-digit',
									})}`;
								}
								
								
								// Overwrite value
								value = humanReadableDateTime;
							}
							
							
							// Simplify and concatenate
							if (value) {
								concatenatedString += ` ${value}`;
							}
						}
						
						
						// Resolve promise with result
						resolve(helperFunctions.simplifyStringForSearching(concatenatedString));
					}, 1);
				})
			);
		}
		
		
		// Wait for all promises to complete
		void Promise.all(promises).then((values) => {
			// Update state
			setSearchIndex(values);
		});
	});
	
	
	// Handle searching
	let mutatedItems = useMemo(() => {
		// Do no work if searching is not needed
		if (!searchQuery || !searchableKeys || searchIndex === null || items === null) {
			return items;
		}
		
		
		// Process search query into words
		const words = helperFunctions.simplifyStringForSearching(searchQuery).split(' ');
		
		
		// Filter down to matches
		return items.filter((_, itemIndex) => words.every((word) => (searchIndex[itemIndex] || '').includes(word)));
	}, [searchQuery, searchableKeys, searchIndex, items]);
	
	
	// Handle sorting
	mutatedItems = useMemo(() => {
		// Set if no sorting exists
		if (!memoizedSortFunction || !sorting || !mutatedItems) {
			return mutatedItems;
		}
		
		
		// Sort the items
		const newItems = clone(mutatedItems);
		newItems.sort((a, b) => memoizedSortFunction(a, b, sorting));
		
		
		// Return
		return newItems;
	}, [mutatedItems, memoizedSortFunction, sorting]);
	
	
	// A function that updates the sorting based on the column, and optionally the initial sort direction
	const setSortingColumn = (column: string, initialDirection?: Sorting['direction']) => {
		if (!sorting) {
			return;
		}
		
		if (column === sorting.column) {
			setSorting({
				column: sorting.column,
				direction: sorting.direction === 'ascending' ? 'descending' : 'ascending',
			});
			
			return;
		}
		
		setSorting({
			column: column,
			direction: initialDirection ?? 'ascending',
		});
	};
	
	
	// Filter the items
	mutatedItems = mutatedItems?.filter(filterFunction ?? (() => true)) ?? null;
	
	
	// Move back to the last page that exists when the list gets shorter
	const lastPageIndex = Math.max(0, Math.ceil((mutatedItems?.length ?? 0) / pageSize) - 1);
	const pageIndexIsPastLastPage = mutatedItems !== null && pageIndex > lastPageIndex;
	const cappedPageIndex = pageIndexIsPastLastPage ? lastPageIndex : pageIndex;
	
	useEffect(() => {
		if (pageIndexIsPastLastPage) {
			setPageIndex(lastPageIndex);
		}
	});
	
	
	// Reset page index when search query changes
	useUpdateOnlyEffect(() => {
		if (pageIndex !== 0) {
			setPageIndex(0);
		}
	}, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Slice the appropriate number of items based on page size
	const slicedItems = mutatedItems?.slice(pageSize * cappedPageIndex, pageSize * (cappedPageIndex + 1)) ?? null;
	
	
	// Create a search field component
	const boundSetSearchQuery = useRef<(query: string) => void>((query) => {
		setSearchQuery(query);
	});
	
	const handleSearchQueryChange = useCallback(debounce(setSearchQuery, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	const searchField = (
		<Input
			type='text'
			name='query'
			label='Search'
			icon={faMagnifyingGlass}
			note={
				searchIndex !== null || (window as typeof window & { STORYBOOK?: true }).STORYBOOK ? undefined : (
					<React.Fragment>
						<FontAwesomeIcon icon={faArrowsRotate} spin size='sm' style={{ opacity: 0.5 }} /> Building search index…
					</React.Fragment>
				)
			}
			onChange={handleSearchQueryChange}
			initialValue={searchQuery}
			bindSetter={(setter) => (boundSetSearchQuery.current = setter)}
		/>
	);
	
	
	// Create a page size chooser
	const handlePageSizeChange = (newValue: string | number) => {
		const newPageSize = Number(newValue);
		
		setPageIndex(Math.floor(cappedPageIndex * (pageSize / newPageSize)));
		setPageSize(newPageSize);
	};
	
	let pageSizeField: React.ReactElement | null = mutatedItems ? (
		<Select
			initialValue={pageSizeAfterFiltering}
			name='pageSize'
			icon={faBarsStaggered}
			onChange={handlePageSizeChange}
			label={`${helperFunctions.ucfirst(pluralDescriptor)} per page`}
			bindSetter={(setter) => (pageSizeSetter.current = setter)}
		>
			{filteredPageSizes.map((pageSize) => (
				<option key={pageSize} value={pageSize}>
					{pageSize} per page
				</option>
			))}
		</Select>
	) : null;
	
	if (filteredPageSizes.length === 1) {
		pageSizeField = null;
	}
	
	
	// Create the pagination component
	let paginationComponent: React.ReactElement | null = mutatedItems ? (
		<Pagination>
			<DisplayingText>
				Displaying {(pageSize * cappedPageIndex + 1).toLocaleString()} to{' '}
				{Math.min(pageSize * (cappedPageIndex + 1), mutatedItems.length).toLocaleString()} of{' '}
				{mutatedItems.length.toLocaleString()} {pluralDescriptor}
			</DisplayingText>
			
			<PageNavigation
				{...pageNavigationProps}
				pageCount={Math.ceil(mutatedItems.length / pageSize)}
				currentPageIndex={cappedPageIndex}
				goToPage={(page: number) => setPageIndex(page)}
			/>
		</Pagination>
	) : null;
	
	if (mutatedItems && mutatedItems.length <= sortedPageSizes[0]) {
		paginationComponent = null;
	}
	
	
	// Return data
	return {
		items: slicedItems,
		unslicedItems: mutatedItems,
		setItems: setItems,
		searchQuery: searchQuery,
		setSearchQuery: boundSetSearchQuery.current,
		sorting: sorting,
		setSortingColumn: setSortingColumn,
		searchField: searchField,
		pageSizeField: pageSizeField,
		paginationComponent: paginationComponent,
	};
}
